import React, { useState } from "react";
import { toast } from "sonner";
import { MoreVertical, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useAddOptOutKeywordMutation,
  useGetOptOutKeywordsQuery,
  useRemoveOptOutKeywordMutation,
} from "@/store/rest_api";
import type { SmsOptOutKeyword } from "@/model/interface/OrganisationSettings";
import { apiErrorMessage } from "@/util/apiError";
import { formatDate } from "@/util/date";

export default function OptOutKeywordsCard() {
  const { data: setting, isLoading } = useGetOptOutKeywordsQuery();
  const [addKeyword, { isLoading: isAdding }] = useAddOptOutKeywordMutation();
  const [removeKeyword] = useRemoveOptOutKeywordMutation();
  const [newKeyword, setNewKeyword] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  function addedByLabel(keyword: SmsOptOutKeyword) {
    const user = keyword.createdByMembership?.user;
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    const addedOn = formatDate(keyword.createdAt);
    return name ? `Added by ${name} · ${addedOn}` : `Added ${addedOn}`;
  }

  function handleKeywordChange(value: string) {
    setNewKeyword(value);
    setAddError(null);
  }

  async function handleAdd() {
    const keyword = newKeyword.trim();
    if (!keyword) {
      return;
    }
    try {
      const added = await addKeyword({ keyword }).unwrap();
      setNewKeyword("");
      toast.success(`${added.keyword} added`);
    } catch (error: unknown) {
      setAddError(apiErrorMessage(error, "Couldn't add the keyword"));
    }
  }

  async function handleRemove(keyword: SmsOptOutKeyword) {
    try {
      await removeKeyword(keyword.id).unwrap();
      toast.success(`${keyword.keyword} removed`, {
        description: "Numbers it already opted out stay opted out.",
      });
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Couldn't remove the keyword"));
    }
  }

  function renderKeywords() {
    if (isLoading) {
      return <Skeleton className="h-24 w-full" />;
    }
    if (!setting || setting.keywords.length === 0) {
      return (
        <div className="rounded-lg border border-dashed px-4 py-5 text-center">
          <p className="text-sm font-medium">No keywords yet</p>
          <p className="text-sm text-muted-foreground">Only the built-in words and phrases opt people out.</p>
        </div>
      );
    }
    return (
      <div className="divide-y rounded-lg border">
        {setting.keywords.map((keyword) => (
          <div key={keyword.id} className="flex items-center gap-3 py-2 pl-4 pr-2">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm font-semibold">{keyword.keyword}</p>
              <p className="truncate text-xs text-muted-foreground">{addedByLabel(keyword)}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Options">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-destructive" onClick={() => void handleRemove(keyword)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove keyword
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Opt-out keywords</CardTitle>
        <CardDescription>
          A reply that contains one of these words or phrases is handled like STOP. The number stops getting texts and
          automated calls.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Always on</p>
          <div className="flex flex-wrap gap-1.5">
            {setting?.builtInKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-md border bg-muted px-2 py-0.5 font-mono text-xs font-semibold text-muted-foreground"
              >
                {keyword}
              </span>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Plus phrases like “stop texting me”, “remove me” and “leave me alone”.
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="opt-out-keyword-input"
            className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Your keywords
          </label>
          <div className="flex gap-2">
            <Input
              id="opt-out-keyword-input"
              value={newKeyword}
              placeholder="Add a word or phrase"
              aria-invalid={addError ? true : undefined}
              onChange={(event) => handleKeywordChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleAdd();
                }
              }}
            />
            <Button onClick={() => void handleAdd()} disabled={isAdding || !newKeyword.trim()}>
              Add
            </Button>
          </div>
          {addError && <p className="text-sm font-medium text-destructive">{addError}</p>}
          {renderKeywords()}
          <p className="text-sm text-muted-foreground">
            Whole words only, anywhere in the reply. Capitals and punctuation don't matter: FU catches “fu!!” but not
            “fun”.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
